/**
 * exam — client feature module (spec §4.1, §4.5, brief §exam).
 * A single top-level path (`/exam`) with nested children — not several
 * top-level routes — so this feature still contributes exactly one entry to
 * `client/src/app/router.test.tsx`'s "matches every route every feature
 * registers" check (frozen: one path per feature), the same pattern
 * `practice/index.ts` uses.
 *
 * The session view owns `useExamMode()`: it sets `examActive` while mounted
 * so the shell hides every nav entry marked `hideDuringExam` (the content
 * feature's topics and past-exam archive declare it; the formula sheet does
 * not, ruling 6).
 */
import { ClipboardList } from "lucide-react";
import type { FeatureModule } from "@client/app/types";
import { ExamGradePage } from "./GradePage";
import { ExamLobbyPage } from "./Page";
import { ExamResultPage } from "./ResultPage";
import { ExamSessionPage } from "./SessionPage";
import { wrapperDict } from "./wrapper/i18n";

const examFeature: FeatureModule = {
  id: "exam",
  routes: [
    {
      path: "/exam",
      children: [
        { index: true, Component: ExamLobbyPage },
        { path: "session", Component: ExamSessionPage },
        { path: ":id/grade", Component: ExamGradePage },
        { path: ":id/result", Component: ExamResultPage },
      ],
    },
  ],
  nav: [{ to: "/exam", labelKey: "exam.nav", icon: ClipboardList, order: 20 }],
  dict: {
    sv: {
      ...wrapperDict.sv,
      "exam.nav": "Övningstenta",
      "exam.lobby.description":
        "Skriv en övningstenta under samma villkor som på den riktiga tentan.",
      "exam.lobby.aids.title": "Tillåtna hjälpmedel",
      "exam.lobby.aids.handbook": "Physics Handbook",
      "exam.lobby.aids.tables":
        "Tabeller och formelsamlingar från kursen (formelsamlingen finns kvar under Bibliotek medan du skriver)",
      "exam.lobby.aids.calculator": "Räknare",
      "exam.lobby.aids.formulaSheet": "Ett eget handskrivet A4-blad med formler",
      "exam.lobby.active.title": "Du har en pågående övningstenta",
      "exam.lobby.active.body": "Fortsätt där du var, eller lämna in det du hunnit med.",
      "exam.lobby.resume": "Fortsätt övningstentan",
      "exam.lobby.start.title": "Starta en övningstenta",
      "exam.lobby.start.thresholds": "Tentan är på {max} p: G vid {g} p, VG vid {vg} p.",
      "exam.lobby.start.unreleased":
        "En slumpad övningstenta kan skapas när fler föreläsningars frågor är öppna. Frågorna öppnas inför varje föreläsning. Tills dess kan du skriva en gammal tenta nedan.",
      "exam.lobby.start.body":
        "En slumpad övningstenta följer samma mönster som de riktiga tentorna: 7 uppgifter, 18 poäng.",
      "exam.lobby.startConfirm.title": "Starta övningstentan nu?",
      "exam.lobby.startConfirm.body":
        "Tiden börjar gå direkt och kan inte pausas. Skrivtid: {duration}.",
      "exam.lobby.startConfirm.confirm": "Starta",
      "exam.lobby.startConfirm.pastExam": "Tenta {term}",
      "exam.lobby.duration.5h": "5 timmar (som riktig tenta)",
      "exam.lobby.duration.3h": "3 timmar",
      "exam.lobby.duration.1h30": "1,5 timmar",
      // A plain label for the confirm dialog's own sentence — review-A minor:
      // interpolating the button's own label ("5 timmar (som riktig tenta)")
      // mid-sentence there read oddly.
      "exam.lobby.durationPlain.5h": "5 timmar",
      "exam.lobby.durationPlain.3h": "3 timmar",
      "exam.lobby.durationPlain.1h30": "1,5 timmar",
      "exam.lobby.pools.title": "Uppgiftsbanken, uppgift för uppgift",
      "exam.lobby.pools.problem": "Uppgift",
      "exam.lobby.pools.points": "Poäng",
      "exam.lobby.pools.chapters": "Moment",
      "exam.lobby.pools.status": "Osedda / totalt",
      "exam.lobby.pools.unseenOfEligible": "{unseen} osedda av {eligible}",
      "exam.lobby.pastExams.title": "Gamla tentor",
      "exam.lobby.pastExams.start": "Skriv {exam}",
      "exam.lobby.practicePapers.title": "Nyskrivna övningstentor",
      "exam.lobby.practicePapers.note":
        "De bygger på kursens uppgiftsbank och är inte officiella tentor, men har samma format och samma skrivtid.",
      "exam.lobby.practicePapers.start": "Skriv {paper}",
      "exam.lobby.requested.ready":
        "{paper} är markerad nedan — klicka på knappen för att starta. Klockan börjar direkt.",
      "exam.lobby.requested.unknown": "Det finns ingen tenta som heter {paper} att skriva.",
      "exam.lobby.requested.busy":
        "Du har redan en pågående övningstenta — lämna in den innan du skriver {paper}.",
      "exam.lobby.history.title": "Tidigare övningstentor",
      "exam.lobby.history.empty": "Du har inte skrivit någon övningstenta ännu.",
      "exam.lobby.history.date": "Datum",
      "exam.lobby.history.kind": "Typ",
      "exam.lobby.history.generated": "Slumpad övningstenta",
      "exam.lobby.history.practicePaper": "Övningstenta {paper}",
      "exam.lobby.history.status": "Status",
      "exam.lobby.history.score": "Poäng",
      "exam.lobby.history.verdict": "Betyg",
      "exam.lobby.history.actions": "Åtgärd",
      "exam.lobby.history.open": "Visa resultat",
      "exam.lobby.history.grade": "Rätta",
      "exam.status.in_progress": "Pågår",
      "exam.status.submitted": "Inlämnad",
      "exam.status.graded": "Rättad",
      "exam.status.expired": "Tiden ute",
      "exam.status.abandoned": "Avbruten",
      "exam.verdict.U": "Underkänt",
      "exam.verdict.G": "Godkänt",
      "exam.verdict.VG": "Väl godkänt",
      "exam.session.srTitle": "Pågående övningstenta",
      "exam.session.instruction":
        "Lös på papper som på riktig tenta — skriv bara in slutsvaret och eventuella anteckningar här.",
      "exam.session.formulas": "Formelsamlingen (öppnas i ny flik)",
      "exam.session.timeRemaining": "Tid kvar",
      "exam.session.warn30Badge": "Under 30 min kvar",
      "exam.session.warn5Badge": "Under 5 min kvar",
      "exam.session.warn1Badge": "Under 1 min kvar",
      "exam.session.timeWarning.one": "{count} minut kvar",
      "exam.session.timeWarning.other": "{count} minuter kvar",
      "exam.session.problemTitle": "Uppgift {slot} av {total}",
      "exam.session.answerLabel": "Svar:",
      "exam.session.notesLabel": "Anteckningar (valfritt)",
      "exam.session.notesHint": "Egna anteckningar till dig själv — rättas inte separat.",
      "exam.session.submit": "Lämna in övningstentan",
      "exam.session.submitConfirm.title": "Lämna in övningstentan?",
      "exam.session.submitConfirm.body": "Du kan inte ändra dina svar efter att du har lämnat in.",
      "exam.session.submitConfirm.confirm": "Lämna in",
      "exam.session.abandon": "Avbryt övningstentan",
      "exam.session.abandonConfirm.title": "Vill du avbryta övningstentan?",
      "exam.session.abandonConfirm.body":
        "Övningstentan avslutas utan att lämnas in och kan inte återupptas. Den rättas inte och räknas inte in i dina framsteg, men syns som avbruten bland dina tidigare övningstentor.",
      "exam.session.abandonConfirm.confirm": "Avbryt övningstentan",
      "exam.session.abandonConfirm.cancel": "Fortsätt skriva",
      "exam.session.abandoned": "Övningstentan är avbruten.",
      "exam.session.restore.title": "Återställ osparade svar?",
      "exam.session.restore.body":
        "Den här enheten har svar som inte hann sparas till servern. Vill du återställa dem?",
      "exam.session.restore.restore": "Återställ",
      "exam.session.restore.discard": "Behåll sparade svar",
      "exam.session.submitSaveFailed":
        "Kunde inte spara de senaste svaren. Övningstentan har inte lämnats in. Försök igen.",
      "exam.session.storedAnswersUsed":
        "Övningstentan avslutades med de senast sparade svaren. Dina osparade svar finns kvar i den lokala kopian.",
      "exam.session.saveError":
        "Kunde inte spara ditt senaste svar — kontrollera anslutningen. Vi försöker igen.",
      "exam.session.sessionExpired":
        "Du har blivit utloggad. Dina svar finns kvar på den här enheten — logga in igen för att fortsätta spara.",
      "exam.grade.title": "Rätta din övningstenta",
      "exam.grade.description": "Jämför ditt eget svar med lösningen och kryssa i det du visade.",
      "exam.grade.runningTotal": "{total} av {max} poäng",
      "exam.grade.pointsLabel": "Poäng",
      "exam.grade.thresholds": "G vid {g} p, VG vid {vg} p",
      "exam.grade.notGraded": "Ej rättad ännu",
      "exam.grade.tickedCount": "{ticked} av {count} bedömningspunkter ikryssade",
      "exam.grade.howTo":
        "Så rättar du: jämför ditt svar med lösningen och kryssa i varje bedömningspunkt du klarade.",
      "exam.grade.yourAnswer": "Ditt svar",
      "exam.grade.yourNotes": "Dina anteckningar",
      "exam.grade.modelSolution": "Lösning",
      "exam.grade.rubricTitle": "Bedömningspunkter",
      "exam.grade.done": "Klar med rättning",
      "exam.grade.autoSubmitted":
        "Skrivtiden tog slut och övningstentan lämnades in automatiskt. Dina sparade svar är med.",
      "exam.result.title": "Resultat",
      "exam.result.lateSubmitted":
        "Skrivtiden var slut när övningstentan lämnades in. Svar du sparat före dess är med.",
      "exam.result.bonusApplied.one": "Varav {bonus} bonuspoäng",
      "exam.result.bonusApplied.other": "Varav {bonus} bonuspoäng",
      "exam.result.thresholds": "G vid {g} p, VG vid {vg} p",
      "exam.result.perProblem": "Poäng per uppgift",
      "exam.result.perChapter": "Poäng per moment",
      "exam.result.score": "Poäng",
      "exam.result.ratio": "Andel",
      "exam.result.practiceWeak.title": "Öva på det som var svagast",
      "exam.result.practiceWeak.body": "Du hade lägst andel poäng i de här momenten.",
      "exam.result.practiceWeak.link": "Öva moment {chapters}",
      "exam.result.practiceWeak.change": "Ändra urval",
    },
    en: {
      ...wrapperDict.en,
      "exam.nav": "Exam",
      "exam.lobby.description":
        "Sit a simulated exam under real conditions, or practice an old paper.",
      "exam.lobby.aids.title": "Allowed aids",
      "exam.lobby.aids.handbook": "Physics Handbook",
      "exam.lobby.aids.tables":
        "Tables and formula sheets from the course (the formula sheet stays in the Library while you write)",
      "exam.lobby.aids.calculator": "Calculator",
      "exam.lobby.aids.formulaSheet": "One hand-written A4 formula sheet of your own",
      "exam.lobby.active.title": "You have an exam in progress",
      "exam.lobby.active.body": "Continue where you left off, or submit what you have.",
      "exam.lobby.resume": "Resume the exam",
      "exam.lobby.start.title": "Start an exam",
      "exam.lobby.start.thresholds": "The exam is {max} p: G at {g} p, VG at {vg} p.",
      "exam.lobby.start.unreleased":
        "A generated practice exam becomes available when more lectures' questions are open. Questions open ahead of each lecture. Until then you can sit a past exam below.",
      "exam.lobby.start.body":
        "The paper is generated at random following the same pattern as the real exams: 7 problems, 18 points.",
      "exam.lobby.startConfirm.title": "Start the exam now?",
      "exam.lobby.startConfirm.body":
        "The clock starts immediately and cannot be paused. Writing time: {duration}.",
      "exam.lobby.startConfirm.confirm": "Start",
      "exam.lobby.startConfirm.pastExam": "Exam {term}",
      "exam.lobby.duration.5h": "5 hours (real exam length)",
      "exam.lobby.duration.3h": "3 hours",
      "exam.lobby.duration.1h30": "1.5 hours",
      "exam.lobby.durationPlain.5h": "5 hours",
      "exam.lobby.durationPlain.3h": "3 hours",
      "exam.lobby.durationPlain.1h30": "1.5 hours",
      "exam.lobby.pools.title": "The question bank per problem",
      "exam.lobby.pools.problem": "Problem",
      "exam.lobby.pools.points": "Points",
      "exam.lobby.pools.chapters": "Modules",
      "exam.lobby.pools.status": "Unseen / total",
      "exam.lobby.pools.unseenOfEligible": "{unseen} unseen of {eligible}",
      "exam.lobby.pastExams.title": "Old exams",
      "exam.lobby.pastExams.start": "Take {exam}",
      "exam.lobby.practicePapers.title": "Newly written practice papers",
      "exam.lobby.practicePapers.note":
        "Built from the course question bank. Not official exams, but the same format and the same time limit.",
      "exam.lobby.practicePapers.start": "Take {paper}",
      "exam.lobby.requested.ready":
        "{paper} is highlighted below — click the button to start. The clock starts immediately.",
      "exam.lobby.requested.unknown": "There is no paper called {paper} to sit.",
      "exam.lobby.requested.busy":
        "You already have an exam in progress — hand it in before sitting {paper}.",
      "exam.lobby.history.title": "Past exams",
      "exam.lobby.history.empty": "You have not sat an exam yet.",
      "exam.lobby.history.date": "Date",
      "exam.lobby.history.kind": "Kind",
      "exam.lobby.history.generated": "Generated",
      "exam.lobby.history.practicePaper": "Practice exam {paper}",
      "exam.lobby.history.status": "Status",
      "exam.lobby.history.score": "Score",
      "exam.lobby.history.verdict": "Verdict",
      "exam.lobby.history.actions": "Action",
      "exam.lobby.history.open": "View result",
      "exam.lobby.history.grade": "Grade",
      "exam.status.in_progress": "In progress",
      "exam.status.submitted": "Submitted",
      "exam.status.graded": "Graded",
      "exam.status.expired": "Time's up",
      "exam.status.abandoned": "Abandoned",
      "exam.verdict.U": "Fail",
      "exam.verdict.G": "Pass",
      "exam.verdict.VG": "Pass with distinction",
      "exam.session.srTitle": "Exam in progress",
      "exam.session.instruction":
        "Solve on paper as in the real exam — only type your final answer and any notes here.",
      "exam.session.formulas": "The formula sheet (opens in a new tab)",
      "exam.session.timeRemaining": "Time remaining",
      "exam.session.warn30Badge": "Under 30 min left",
      "exam.session.warn5Badge": "Under 5 min left",
      "exam.session.warn1Badge": "Under 1 min left",
      "exam.session.timeWarning.one": "{count} minute left",
      "exam.session.timeWarning.other": "{count} minutes left",
      "exam.session.problemTitle": "Problem {slot} of {total}",
      "exam.session.answerLabel": "Answer:",
      "exam.session.notesLabel": "Notes (optional)",
      "exam.session.notesHint": "Notes to yourself — not graded separately.",
      "exam.session.submit": "Submit the exam",
      "exam.session.submitConfirm.title": "Submit the exam?",
      "exam.session.submitConfirm.body": "You cannot change your answers after submitting.",
      "exam.session.submitConfirm.confirm": "Submit",
      "exam.session.abandon": "Abandon the exam",
      "exam.session.abandonConfirm.title": "Abandon the exam?",
      "exam.session.abandonConfirm.body":
        "The exam ends without being handed in and cannot be resumed. It is not graded and does not count towards your progress, but it is listed as abandoned among your past exams.",
      "exam.session.abandonConfirm.confirm": "Abandon the exam",
      "exam.session.abandonConfirm.cancel": "Keep writing",
      "exam.session.abandoned": "The exam has been abandoned.",
      "exam.session.restore.title": "Restore unsaved answers?",
      "exam.session.restore.body":
        "This device has answers that never made it to the server. Restore them?",
      "exam.session.restore.restore": "Restore",
      "exam.session.restore.discard": "Keep the saved answers",
      "exam.session.submitSaveFailed":
        "Could not save the latest answers. The exam has not been submitted. Try again.",
      "exam.session.storedAnswersUsed":
        "The exam ended using the last saved answers. Your unsaved answers remain in the local copy.",
      "exam.session.saveError":
        "Could not save your last answer — check your connection. We'll keep trying.",
      "exam.session.sessionExpired":
        "You've been signed out. Your answers are still on this device — sign in again to keep saving.",
      "exam.grade.title": "Grade your exam",
      "exam.grade.description":
        "Compare your own answer with the solution and tick what you showed.",
      "exam.grade.runningTotal": "{total} of {max} points",
      "exam.grade.pointsLabel": "Points",
      "exam.grade.thresholds": "G at {g} p, VG at {vg} p",
      "exam.grade.notGraded": "Not graded yet",
      "exam.grade.tickedCount": "{ticked} of {count} rubric points ticked",
      "exam.grade.howTo":
        "How to grade: compare your answer with the solution and tick every rubric point you met.",
      "exam.grade.yourAnswer": "Your answer",
      "exam.grade.yourNotes": "Your notes",
      "exam.grade.modelSolution": "Solution",
      "exam.grade.rubricTitle": "Rubric",
      "exam.grade.done": "Done grading",
      "exam.grade.autoSubmitted":
        "Time ran out and the exam was submitted automatically. Your saved answers are included.",
      "exam.result.title": "Result",
      "exam.result.lateSubmitted":
        "Time was up when the exam was submitted. Answers you saved before then are included.",
      "exam.result.bonusApplied.one": "Of which {bonus} bonus point",
      "exam.result.bonusApplied.other": "Of which {bonus} bonus points",
      "exam.result.thresholds": "G at {g} p, VG at {vg} p",
      "exam.result.perProblem": "Points per problem",
      "exam.result.perChapter": "Points per module",
      "exam.result.score": "Score",
      "exam.result.ratio": "Share",
      "exam.result.practiceWeak.title": "Practice your weakest modules",
      "exam.result.practiceWeak.body": "You scored the lowest share of points in these modules.",
      "exam.result.practiceWeak.link": "Practice modules {chapters}",
      "exam.result.practiceWeak.change": "Change the selection",
    },
  },
};

export default examFeature;
